// Static menu content. IDs, layouts, roles, and icons are independent of visible copy.
export type NavigationItemLink = {
  id: string;
  title: string;
  link: string;
  external?: boolean;
  subtitle?: string;
  badge?: string;
  role?: "overview";
};

export type NavigationSection = {
  id: string;
  title?: string;
  description?: string;
  mobilePresentation: "drilldown" | "inline";
  icon?: "darkplane" | "observability" | "agent0" | "signalstore";
  links?: NavigationItemLink[];
  groups?: { id: string; title: string; links: NavigationItemLink[] }[];
  promo?: { title: string; subtitle?: string; image: string; link: string };
};

export type NavigationItem =
  | {
      id: string;
      title: string;
      layout: "link";
      link: string;
      external?: boolean;
    }
  | {
      id: string;
      title: string;
      layout: "product" | "resources" | "about";
      sections: NavigationSection[];
      footerLabel?: string;
      footerCta?: { title: string; link: string };
    };

export const navigation = [
  {
    id: "product",
    title: "Product ",
    layout: "product",
    footerLabel: "Explore what's new in the product",
    footerCta: {
      link: "/changelog",
      title: "Changelog",
    },
    sections: [
      {
        id: "darkplane",
        mobilePresentation: "drilldown",
        title: "Darkplane",
        description: "Build with AI. Ship with confidence",
        icon: "darkplane",
        links: [
          {
            id: "overview",
            title: "Overview",
            link: "/darkplane",
            role: "overview",
          },
          {
            id: "ai-sdlc-insights",
            title: "AI SDLC Insights",
            link: "/darkplane-ai-sdlc-insights",
            subtitle: "Measure AI coding productivity",
          },
          {
            id: "automerge",
            title: "Automerge",
            link: "/darkplane-automerge",
            subtitle: "Approve the safe pull requests",
          },
        ],
        promo: {
          image:
            "/images/navigation/darkplane-promo.webp",
          link: "/darkplane",
          subtitle: "The control room for your AI factory",
          title: "Darkplane",
        },
      },
      {
        id: "observability",
        mobilePresentation: "drilldown",
        title: "Observability",
        description: "Observability without the headache",
        icon: "observability",
        links: [
          {
            id: "overview",
            title: "Overview",
            link: "/observability",
            role: "overview",
          },
        ],
        groups: [
          {
            id: "experience",
            title: "Experience",
            links: [
              {
                id: "web-monitoring",
                title: "Web Monitoring",
                link: "/website-monitoring",
                subtitle: "Understand every session",
              },
              {
                id: "session-replay",
                title: "Session Replay",
                link: "/session-replay",
                subtitle: "See what the user saw",
              },
              {
                id: "synthetic-monitoring",
                title: "Synthetic Monitoring",
                link: "/synthetic-monitoring",
                subtitle: "Catch issues before users do",
              },
            ],
          },
          {
            id: "app-service",
            title: "App & Service",
            links: [
              {
                id: "apm",
                title: "APM",
                link: "/application-performance-management",
                subtitle: "Optimize with clarity",
              },
              {
                id: "distributed-tracing",
                title: "Distributed Tracing",
                link: "/distributed-tracing",
                subtitle: "See every request",
              },
              {
                id: "service-map",
                title: "Service Map",
                link: "/service-map",
                subtitle: "Visualize service dependencies",
              },
              {
                id: "node-js-monitoring",
                title: "Node.js Monitoring",
                link: "/node-js-monitoring",
                subtitle: "Spot your event loop blocks",
              },
              {
                id: "java-monitoring",
                title: "Java Monitoring",
                link: "/java-monitoring",
                subtitle: "Speaks JVM natively",
              },
              {
                id: "python-monitoring",
                title: "Python Monitoring",
                link: "/python-monitoring",
                subtitle: "From request to root cause",
              },
              {
                id: "serverless-monitoring",
                title: "Serverless Monitoring",
                link: "/serverless-monitoring",
                subtitle: "All the way to root cause",
              },
              {
                id: "ruby-monitoring",
                title: "Ruby Monitoring",
                link: "/ruby-monitoring",
                subtitle: "Faster root cause analysis",
              },
              {
                id: "gpu-profiling",
                title: "GPU Profiling",
                link: "/gpu-profiling",
                subtitle: "Maximize GPU efficiency",
              },
              {
                id: "continuous-profiling",
                title: "Continuous Profiling",
                link: "/continuous-profiling",
                subtitle: "Production ready profiling",
              },
            ],
          },
          {
            id: "infrastructure",
            title: "Infrastructure",
            links: [
              {
                id: "infrastructure-monitoring",
                title: "Infrastructure Monitoring",
                link: "/infrastructure-monitoring",
                subtitle: "Stay ahead of issues",
              },
              {
                id: "kubernetes-monitoring",
                title: "Kubernetes Monitoring",
                link: "/kubernetes-monitoring",
                subtitle: "As native as it can get",
              },
              {
                id: "google-cloud-monitoring",
                title: "Google Cloud Monitoring",
                link: "/google-cloud-monitoring",
                subtitle: "Ready when you connect",
              },
              {
                id: "aws-integration",
                title: "AWS Integration",
                link: "/aws-integration",
                subtitle: "Connect, discover, instrument",
              },
            ],
          },
        ],
        promo: {
          image:
            "/images/navigation/observability-promo.webp",
          link: "/observability",
          subtitle: "Full visibility across production",
          title: "Observability",
        },
      },
      {
        id: "agent0",
        mobilePresentation: "drilldown",
        title: "Agent0",
        description: "Automate and fix",
        icon: "agent0",
        links: [
          {
            id: "overview",
            title: "Overview",
            link: "/agent0",
            role: "overview",
          },
          {
            id: "chat",
            title: "Chat",
            link: "/agent0-chat",
            subtitle: "Get the reason",
          },
          {
            id: "automations",
            title: "Automations",
            link: "/agent0-automations",
            subtitle: "No human in the loop",
          },
          {
            id: "auto-tune",
            title: "Auto-tune",
            link: "/auto-tune",
            subtitle: "Let Agent0 handle it",
          },
        ],
        promo: {
          image:
            "/images/navigation/agent0-promo.jpg",
          link: "/agent0",
          subtitle: "Run production on autopilot",
          title: "Agent0",
        },
      },
      {
        id: "signalstore",
        mobilePresentation: "drilldown",
        title: "SignalStore",
        description: "Own your data",
        icon: "signalstore",
        links: [
          {
            id: "overview",
            title: "Overview",
            link: "/signalstore",
            role: "overview",
          },
          {
            id: "log-management",
            title: "Log Management",
            link: "/log-management",
            subtitle: "Smarter logs, faster answers",
          },
          {
            id: "dashboards",
            title: "Dashboards",
            link: "/dashboards",
            subtitle: "Flexible and easy to make",
          },
          {
            id: "alerting",
            title: "Alerting",
            link: "/alerting",
            subtitle: "Your alerts, more powerful",
          },
          {
            id: "integrations",
            title: "Integrations",
            link: "/integrations",
            subtitle: "Start monitoring in minutes",
          },
          {
            id: "cost-control",
            title: "Cost Control",
            link: "/transparent-cost",
            subtitle: "Pay only for what you use",
          },
          {
            id: "configuration-as-code",
            title: "Configuration as Code",
            link: "/observability-as-code",
            subtitle: "The GitOps way",
          },
          {
            id: "signalcontrol",
            title: "SignalControl",
            link: "/signalcontrol",
            subtitle: "The ingestion pipeline",
          },
          {
            id: "slos",
            title: "SLOs",
            link: "/service-level-objectives",
            subtitle: "Service Level Objectives",
          },
        ],
        promo: {
          image:
            "/images/navigation/signalstore-promo.webp",
          link: "/signalstore",
          subtitle: "The OpenTelemetry-native data platform",
          title: "SignalStore",
        },
      },
    ],
  },
  {
    id: "pricing",
    title: "Pricing",
    layout: "link",
    link: "/pricing",
  },
  {
    id: "docs",
    title: "Docs",
    layout: "link",
    link: "/docs/dash0",
  },
  {
    id: "resources",
    title: "Resources",
    layout: "resources",
    footerLabel: "How Zalando keeps a reliable experience",
    footerCta: {
      link: "/customers/finding-the-needle-in-a-trillion-log-haystack-how-zalando-keeps-a-reliable-experience-at-scale",
      title: "Watch video",
    },
    sections: [
      {
        id: "resources-1",
        mobilePresentation: "inline",
        links: [
          {
            id: "guides",
            title: "Guides",
            link: "/guides",
            subtitle: "Expand your knowledge",
          },
          {
            id: "blog",
            title: "Blog",
            link: "/blog",
            subtitle: "Latest in observability",
          },
          {
            id: "knowledge",
            title: "Knowledge",
            link: "/knowledge",
            subtitle: "Core concepts explained",
          },
          {
            id: "customers",
            title: "Customers",
            link: "/customers",
            subtitle: "Trusted by engineers",
          },
          {
            id: "comparisons",
            title: "Comparisons",
            link: "/comparisons",
            subtitle: "Similar products analysis",
          },
          {
            id: "faq",
            title: "FAQ",
            link: "/faq",
            subtitle: "Answer your questions",
          },
          {
            id: "podcast",
            title: "Podcast",
            link: "/podcast",
            subtitle: "Explore tech insights",
          },
          {
            id: "newsletter",
            title: "Newsletter",
            link: "/newsletter",
            subtitle: "Bi-weekly digest",
          },
          {
            id: "opentelemetry-ebook",
            title: "OpenTelemetry eBook",
            link: "/ebooks/opentelemetry-for-dummies",
            subtitle: "Dash0 Special Edition",
          },
          {
            id: "changelog",
            title: "Changelog",
            link: "/changelog",
            subtitle: "Recent improvements",
          },
        ],
      },
      {
        id: "resources-2",
        mobilePresentation: "inline",
        links: [
          {
            id: "integrations",
            title: "Integrations",
            link: "/hub/integrations",
            subtitle: "Connect your whole stack",
          },
          {
            id: "events",
            title: "Events",
            link: "/events",
            subtitle: "Meet us live",
          },
          {
            id: "dash0-for-startups",
            title: "Dash0 for Startups",
            link: "/dash0-for-startups",
            subtitle: "Offer for high-growth startups",
          },
        ],
        promo: {
          image:
            "/images/navigation/beyond-observability-promo.png",
          link: "/blog/beyond-observability",
          subtitle: "Blog post",
          title: "Beyond observability",
        },
      },
    ],
  },
  {
    id: "about",
    title: "About",
    layout: "about",
    sections: [
      {
        id: "about-1",
        mobilePresentation: "inline",
        links: [
          {
            id: "contact-us",
            title: "Contact us",
            link: "/contact",
          },
          {
            id: "about-us",
            title: "About us",
            link: "/about",
          },
          {
            id: "careers",
            title: "Careers",
            link: "https://jobs.ashbyhq.com/dash0",
            external: true,
          },
          {
            id: "security",
            title: "Security",
            link: "/security",
          },
        ],
      },
    ],
  },
] satisfies NavigationItem[];
